<template>
  <div>
    <v-container fluid>
      <v-toolbar color="primary" class="header-title text-white" data-testid="tax-config-v2-edit-toolbar">
        <v-btn icon variant="text" data-testid="edit-back-btn" @click="goBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="title">
          Tax Configuration V2 — {{ taxYear }} / {{ versionCode }}
          <v-chip
            v-if="versionActive"
            data-testid="cfg-v2-active-chip"
            color="success"
            variant="elevated"
            size="x-small"
            class="ml-2"
            prepend-icon="mdi-check-circle"
          >
            active
          </v-chip>
        </v-toolbar-title>
        <v-spacer />
        <v-chip
          v-if="dirty"
          data-testid="cfg-v2-unsaved-chip"
          color="warning"
          variant="elevated"
          size="small"
          class="mr-3"
          prepend-icon="mdi-content-save-alert"
        >
          ยังไม่บันทึก
        </v-chip>
      </v-toolbar>

      <v-card class="card2 mt-1">
        <v-card-text>
          <div class="d-flex align-center flex-wrap ga-2">
            <div class="text-subtitle-1 font-weight-medium">
              <v-icon class="mr-1">mdi-file-cog-outline</v-icon>
              หมวด &amp; กติกา (Categories &amp; Rules)
            </div>
            <v-spacer />
            <v-btn
              data-testid="add-category-btn"
              color="primary"
              variant="outlined"
              size="small"
              prepend-icon="mdi-tag-plus-outline"
              rounded="lg"
              :disabled="versionId == null"
              @click="openAddCategory"
            >
              เพิ่มหมวด
            </v-btn>
          </div>
        </v-card-text>

        <v-card-text v-if="loading">
          <v-skeleton-loader type="heading, list-item-two-line, table-row@3, heading, table-row@2" />
        </v-card-text>

        <v-card-text v-else>
          <!-- Empty: no categories AND no uncategorized rules -->
          <div
            v-if="!workingCategories.length && !uncategorizedRules.length"
            class="empty-block"
            data-testid="edit-empty"
          >
            <v-icon size="56" color="grey-lighten-1">mdi-tag-off-outline</v-icon>
            <p class="empty-title">ยังไม่มีหมวด — เพิ่มหมวดแรก</p>
            <v-btn color="primary" variant="tonal" prepend-icon="mdi-plus" @click="openAddCategory">
              เพิ่มหมวด
            </v-btn>
          </div>

          <v-expansion-panels
            v-else
            v-model="openPanels"
            multiple
            variant="accordion"
            data-testid="category-panels"
          >
            <!-- Category panels -->
            <v-expansion-panel
              v-for="cat in sortedCategories"
              :key="cat.categoryCode"
              :value="cat.categoryCode"
              :data-testid="`category-panel-${cat.categoryCode}`"
            >
              <v-expansion-panel-title>
                <div class="d-flex align-center ga-2 flex-grow-1">
                  <span class="font-weight-bold text-primary">{{ cat.categoryCode }}</span>
                  <span class="text-medium-emphasis">{{ cat.label || '' }}</span>
                  <v-chip size="x-small" variant="tonal" color="primary">
                    {{ rulesOf(cat.categoryCode).length }} rules
                  </v-chip>
                  <v-chip v-if="!cat.isActive" size="x-small" variant="tonal" color="grey">inactive</v-chip>
                </div>
                <template #actions="{ expanded }">
                  <div class="d-flex align-center ga-1" @click.stop>
                    <v-btn
                      :data-testid="`edit-category-${cat.categoryCode}`"
                      icon
                      variant="text"
                      size="small"
                      color="warning"
                      aria-label="แก้ไขหมวด"
                      @click.stop="openEditCategory(cat)"
                    >
                      <v-icon size="18">mdi-pencil-outline</v-icon>
                    </v-btn>
                    <v-btn
                      :data-testid="`delete-category-${cat.categoryCode}`"
                      icon
                      variant="text"
                      size="small"
                      color="error"
                      aria-label="ลบหมวด"
                      @click.stop="openDeleteCategory(cat)"
                    >
                      <v-icon size="18">mdi-delete-outline</v-icon>
                    </v-btn>
                    <v-icon>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </div>
                </template>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="d-flex justify-end mb-2">
                  <v-btn
                    :data-testid="`add-rule-${cat.categoryCode}`"
                    size="small"
                    variant="tonal"
                    color="primary"
                    prepend-icon="mdi-plus"
                    @click="openAddRule(cat.categoryCode)"
                  >
                    เพิ่มกติกา
                  </v-btn>
                </div>
                <v-table class="my-data-table rules-v2-table" density="comfortable">
                  <thead>
                    <tr>
                      <th class="text-left">Rule</th>
                      <th class="text-left">ValueType</th>
                      <th class="text-center">Active</th>
                      <th class="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!rulesOf(cat.categoryCode).length">
                      <td colspan="4" class="text-center text-medium-emphasis py-4" data-testid="rules-empty-row">
                        ยังไม่มีกติกา
                      </td>
                    </tr>
                    <tr
                      v-for="row in rulesOf(cat.categoryCode)"
                      :key="row._uid"
                      :data-testid="`rule-row-${row.fieldKey}`"
                    >
                      <td>
                        <div class="d-flex flex-column">
                          <strong>{{ row.fieldKey }}</strong>
                          <span class="text-caption text-medium-emphasis">{{ row.label }}</span>
                        </div>
                      </td>
                      <td><v-chip size="x-small" variant="tonal" color="info">{{ row.valueType }}</v-chip></td>
                      <td class="text-center">
                        <v-icon :color="row.isActive !== false ? 'success' : 'grey'" size="18">
                          {{ row.isActive !== false ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                        </v-icon>
                      </td>
                      <td class="text-center">
                        <v-btn
                          icon variant="text" size="small" color="warning"
                          :data-testid="`edit-rule-${row.fieldKey}`"
                          aria-label="แก้ไขกติกา"
                          @click="openEditRule(row)"
                        >
                          <v-icon size="18">mdi-pencil-outline</v-icon>
                        </v-btn>
                        <v-btn
                          icon variant="text" size="small" color="error"
                          :data-testid="`delete-rule-${row.fieldKey}`"
                          aria-label="ลบกติกา"
                          @click="openDeleteRule(row)"
                        >
                          <v-icon size="18">mdi-delete-outline</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <!-- Uncategorized bucket (fail-open rules whose category matches nothing) -->
            <v-expansion-panel
              v-if="uncategorizedRules.length"
              value="__UNCAT__"
              data-testid="category-panel-uncategorized"
            >
              <v-expansion-panel-title>
                <div class="d-flex align-center ga-2 flex-grow-1">
                  <span class="font-weight-bold text-grey">(uncategorized)</span>
                  <v-chip size="x-small" variant="tonal" color="grey">
                    {{ uncategorizedRules.length }} rules
                  </v-chip>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="d-flex justify-end mb-2">
                  <v-btn
                    data-testid="add-rule-uncategorized"
                    size="small"
                    variant="tonal"
                    color="primary"
                    prepend-icon="mdi-plus"
                    @click="openAddRule(null)"
                  >
                    เพิ่มกติกา
                  </v-btn>
                </div>
                <v-table class="my-data-table rules-v2-table" density="comfortable">
                  <thead>
                    <tr>
                      <th class="text-left">Rule</th>
                      <th class="text-left">ValueType</th>
                      <th class="text-center">Active</th>
                      <th class="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in uncategorizedRules"
                      :key="row._uid"
                      :data-testid="`rule-row-${row.fieldKey}`"
                    >
                      <td>
                        <div class="d-flex flex-column">
                          <strong>{{ row.fieldKey }}</strong>
                          <span class="text-caption text-medium-emphasis">{{ row.label }}</span>
                        </div>
                      </td>
                      <td><v-chip size="x-small" variant="tonal" color="info">{{ row.valueType }}</v-chip></td>
                      <td class="text-center">
                        <v-icon :color="row.isActive !== false ? 'success' : 'grey'" size="18">
                          {{ row.isActive !== false ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                        </v-icon>
                      </td>
                      <td class="text-center">
                        <v-btn
                          icon variant="text" size="small" color="warning"
                          :data-testid="`edit-rule-${row.fieldKey}`"
                          aria-label="แก้ไขกติกา"
                          @click="openEditRule(row)"
                        >
                          <v-icon size="18">mdi-pencil-outline</v-icon>
                        </v-btn>
                        <v-btn
                          icon variant="text" size="small" color="error"
                          :data-testid="`delete-rule-${row.fieldKey}`"
                          aria-label="ลบกติกา"
                          @click="openDeleteRule(row)"
                        >
                          <v-icon size="18">mdi-delete-outline</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Sticky Save-all bar -->
    <div class="save-bar" data-testid="save-bar">
      <v-container fluid class="d-flex align-center py-2">
        <v-chip
          v-if="versionActive"
          color="warning"
          variant="tonal"
          size="small"
          prepend-icon="mdi-alert"
        >
          เวอร์ชันนี้กำลังใช้งานจริง (LIVE)
        </v-chip>
        <v-spacer />
        <v-btn variant="outlined" class="mr-2" prepend-icon="mdi-arrow-left" @click="goBack">
          Back
        </v-btn>
        <v-btn
          data-testid="save-all-btn"
          color="primary"
          variant="flat"
          prepend-icon="mdi-content-save"
          :loading="saving"
          :disabled="saving || loading || versionId == null"
          @click="saveAll"
        >
          Save all
        </v-btn>
      </v-container>
    </div>

    <!-- Category dialog -->
    <TaxCategoryV2Dialog
      v-model="categoryDialog"
      :initial-data="categoryEditTarget"
      :next-sort-order="workingCategories.length + 1"
      @submit="submitCategory"
    />

    <!-- Rule dialog -->
    <TaxConfigV2Dialog
      v-model="ruleDialog"
      mode="rule"
      :initial-data="ruleEditTarget"
      :categories="workingCategories"
      :existing-field-keys="allFieldKeys"
      @submit="submitRule"
    />

    <!-- Delete confirm (category or rule) -->
    <v-dialog v-model="deleteDialog" max-width="520px" persistent data-testid="edit-delete-dialog">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-alert-circle</v-icon>
          ยืนยันการลบ
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            {{ deleteKind === 'category'
              ? 'ลบหมวดนี้ — กติกาในหมวดจะกลายเป็น (uncategorized)'
              : 'ลบกติกานี้ — จะมีผลเมื่อกด Save all' }}
          </v-alert>
          <p class="font-weight-bold text-primary">{{ deleteTargetLabel }}</p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn data-testid="edit-delete-cancel-btn" variant="outlined" @click="deleteDialog = false">
            <v-icon start>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            data-testid="edit-delete-confirm-btn"
            color="error"
            variant="elevated"
            :loading="deletingCategory"
            @click="confirmDelete"
          >
            <v-icon start>mdi-delete</v-icon>
            ลบ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" location="top" timeout="3500">
      {{ snackbarMessage }}
      <template #actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { taxConfigV2Api } from '../services/taxConfigV2Api';
import TaxCategoryV2Dialog from '../components/TaxCategoryV2Dialog.vue';
import TaxConfigV2Dialog from '../components/TaxConfigV2Dialog.vue';

// A TaxConfigRuleV2 row is a FIELD declaration, not a tax rule: FieldKey + Label + Category +
// ValueType. ValueType is the data type of the value the field holds; anything unknown → text.
const VALUE_TYPES = ['text', 'number', 'boolean'];

function normalizeValueType(v) {
  const t = String(v || '').trim().toLowerCase();
  return VALUE_TYPES.includes(t) ? t : 'text';
}

let UID = 1;

export default {
  name: 'TaxConfigurationV2Edit',
  components: { TaxCategoryV2Dialog, TaxConfigV2Dialog },
  data() {
    return {
      loading: false,
      saving: false,
      dirty: false,
      yearId: null,
      versionId: null,
      versionActive: false,
      workingCategories: [], // [{ categoryID, categoryCode, label, sortOrder, isActive }]
      workingRules: [], // flat; each carries _uid + categoryCode
      serverRuleCount: 0, // rules the last load saw on the server — guards a wipe-everything save
      openPanels: [],
      // category dialog
      categoryDialog: false,
      categoryEditTarget: null,
      deletingCategory: false,
      // rule dialog
      ruleDialog: false,
      ruleEditTarget: null,
      addRuleCategoryCode: null,
      editingRuleUid: null,
      // delete
      deleteDialog: false,
      deleteKind: '', // 'category' | 'rule'
      deleteTarget: null,
      // snackbar
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
    };
  },
  computed: {
    taxYear() {
      return String(this.$route.params.taxYear || '').toUpperCase();
    },
    versionCode() {
      return String(this.$route.params.versionCode || '').toUpperCase();
    },
    categoryCodeSet() {
      return new Set(this.workingCategories.map((c) => c.categoryCode));
    },
    sortedCategories() {
      return this.workingCategories.slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    },
    uncategorizedRules() {
      const set = this.categoryCodeSet;
      return this.workingRules.filter((r) => !r.categoryCode || !set.has(r.categoryCode));
    },
    allFieldKeys() {
      return this.workingRules.map((r) => r.fieldKey).filter(Boolean);
    },
    deleteTargetLabel() {
      if (!this.deleteTarget) return '';
      if (this.deleteKind === 'category') {
        return `${this.deleteTarget.categoryCode}${this.deleteTarget.label ? ' - ' + this.deleteTarget.label : ''}`;
      }
      return `${this.deleteTarget.fieldKey}${this.deleteTarget.label ? ' - ' + this.deleteTarget.label : ''}`;
    },
  },
  mounted() {
    this.resolveAndLoad();
    window.addEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty && !window.confirm('ละทิ้งการแก้ไขที่ยังไม่บันทึก?')) {
      next(false);
      return;
    }
    next();
  },
  methods: {
    onBeforeUnload(e) {
      if (this.dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    async resolveAndLoad() {
      this.loading = true;
      try {
        // Route params are CODES, not ids — resolve versionId via code → year → versions.
        const year = await taxConfigV2Api.getYearByCode(this.taxYear);
        if (!year || year.yearID == null) {
          this.showError(`ไม่พบปีภาษี ${this.taxYear}`);
          this.$router.replace('/TaxConfigurationV2');
          return;
        }
        this.yearId = year.yearID;
        const versions = await taxConfigV2Api.getVersions(year.yearID, true);
        const version = (versions || []).find(
          (v) => String(v.versionCode || '').toUpperCase() === this.versionCode,
        );
        if (!version) {
          this.showError(`ไม่พบเวอร์ชัน ${this.versionCode}`);
          this.$router.replace('/TaxConfigurationV2');
          return;
        }
        this.versionId = version.versionID;
        this.versionActive = Boolean(version.isActive);
        const full = await taxConfigV2Api.getFullConfig(this.versionId, true);
        this.buildWorkingSet(full);
        this.dirty = false;
      } catch (err) {
        this.showError(err?.message || 'โหลดคอนฟิกไม่สำเร็จ');
      } finally {
        this.loading = false;
      }
    },
    mapRule(dto, fallbackCategoryCode) {
      return {
        _uid: UID++,
        ruleID: dto.ruleID ?? null,
        versionID: dto.versionID ?? this.versionId,
        categoryCode: dto.categoryCode || fallbackCategoryCode || null,
        fieldKey: dto.fieldKey || '',
        label: dto.label || '',
        valueType: normalizeValueType(dto.valueType),
        sortOrder: dto.sortOrder ?? 0,
        isActive: dto.isActive !== false,
      };
    },
    buildWorkingSet(full) {
      // `GET versions/{id}/full` returns a FLAT payload — { version, categories[], rules[], warnings }.
      // Rules are NOT nested under their category; they soft-link by CategoryCode (there is no FK).
      const cats = ((full && full.categories) || [])
        .filter((c) => c && c.categoryID != null && c.categoryID > 0)
        .map((c) => ({
          categoryID: c.categoryID,
          categoryCode: c.categoryCode,
          label: c.label || '',
          sortOrder: c.sortOrder ?? 0,
          isActive: c.isActive !== false,
        }));
      const knownCodes = new Set(cats.map((c) => String(c.categoryCode || '').toUpperCase()));
      const rules = ((full && full.rules) || []).map((r) => {
        const mapped = this.mapRule(r, null);
        // A rule whose CategoryCode no longer resolves (server reports it in `warnings`) drops into
        // the uncategorized bucket instead of vanishing into a panel that does not exist.
        const code = String((r && r.categoryCode) || '').toUpperCase();
        mapped.categoryCode = code && knownCodes.has(code) ? r.categoryCode : null;
        return mapped;
      });
      this.workingCategories = cats;
      this.workingRules = rules;
      this.serverRuleCount = rules.length;
      this.openPanels = [...cats.map((c) => c.categoryCode), '__UNCAT__'];

      const warnings = (full && full.warnings) || [];
      if (warnings.length) this.showError(warnings.join(' · '));
    },
    rulesOf(categoryCode) {
      return this.workingRules
        .filter((r) => r.categoryCode === categoryCode)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    },
    // ── Categories (persist immediately, optimistic local so staged rules survive) ──
    openAddCategory() {
      this.categoryEditTarget = null;
      this.categoryDialog = true;
    },
    openEditCategory(cat) {
      this.categoryEditTarget = { ...cat };
      this.categoryDialog = true;
    },
    async submitCategory(form) {
      const isEdit = form.categoryID != null;
      try {
        if (isEdit) {
          const dto = await taxConfigV2Api.updateCategory(form.categoryID, {
            categoryCode: form.categoryCode,
            label: form.label,
            sortOrder: form.sortOrder,
            isActive: form.isActive,
          });
          const idx = this.workingCategories.findIndex((c) => c.categoryID === form.categoryID);
          if (idx >= 0) {
            this.workingCategories.splice(idx, 1, {
              ...this.workingCategories[idx],
              ...(dto || form),
            });
          }
          this.showSuccess(`อัปเดตหมวด ${form.categoryCode} แล้ว`);
        } else {
          const dto = await taxConfigV2Api.createCategory(this.versionId, {
            categoryCode: form.categoryCode,
            label: form.label,
            sortOrder: form.sortOrder,
            isActive: form.isActive,
          });
          this.workingCategories.push({
            categoryID: (dto && dto.categoryID) ?? null,
            categoryCode: (dto && dto.categoryCode) || form.categoryCode,
            label: (dto && dto.label) ?? form.label,
            sortOrder: (dto && dto.sortOrder) ?? form.sortOrder,
            isActive: (dto && dto.isActive) ?? form.isActive,
          });
          this.openPanels = [...new Set([...this.openPanels, form.categoryCode])];
          this.showSuccess(`สร้างหมวด ${form.categoryCode} แล้ว`);
        }
        this.categoryDialog = false;
        this.categoryEditTarget = null;
      } catch (err) {
        this.showError(err?.message || 'บันทึกหมวดไม่สำเร็จ');
      }
    },
    openDeleteCategory(cat) {
      this.deleteKind = 'category';
      this.deleteTarget = cat;
      this.deleteDialog = true;
    },
    // ── Rules (staged; persisted by Save all bulk) ──
    openAddRule(categoryCode) {
      this.addRuleCategoryCode = categoryCode;
      this.editingRuleUid = null;
      this.ruleEditTarget = null;
      this.ruleDialog = true;
    },
    openEditRule(rule) {
      this.editingRuleUid = rule._uid;
      this.addRuleCategoryCode = null;
      this.ruleEditTarget = { ...rule };
      this.ruleDialog = true;
    },
    submitRule(payload) {
      if (this.editingRuleUid != null) {
        const idx = this.workingRules.findIndex((r) => r._uid === this.editingRuleUid);
        if (idx >= 0) {
          this.workingRules.splice(idx, 1, { ...this.workingRules[idx], ...payload });
        }
      } else {
        const categoryCode = payload.categoryCode ?? this.addRuleCategoryCode ?? null;
        // Guard: unique FieldKey within version.
        if (this.workingRules.some((r) => r.fieldKey === payload.fieldKey)) {
          this.showError(`Field Key ${payload.fieldKey} มีอยู่แล้วในเวอร์ชันนี้`);
          return;
        }
        this.workingRules.push({ _uid: UID++, ...payload, categoryCode });
      }
      this.dirty = true;
      this.ruleDialog = false;
      this.ruleEditTarget = null;
      this.editingRuleUid = null;
      this.addRuleCategoryCode = null;
      this.showSuccess('บันทึกกติกาในรายการแล้ว — กด Save all เพื่อบันทึกถาวร');
    },
    openDeleteRule(rule) {
      this.deleteKind = 'rule';
      this.deleteTarget = rule;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      if (!this.deleteTarget) return;
      if (this.deleteKind === 'rule') {
        const idx = this.workingRules.findIndex((r) => r._uid === this.deleteTarget._uid);
        if (idx >= 0) this.workingRules.splice(idx, 1);
        this.dirty = true;
        this.deleteDialog = false;
        this.deleteTarget = null;
        this.showSuccess('ลบกติกาออกจากรายการแล้ว — กด Save all เพื่อบันทึกถาวร');
        return;
      }
      // category delete → API + optimistic (its rules become uncategorized)
      this.deletingCategory = true;
      try {
        const cat = this.deleteTarget;
        if (cat.categoryID != null) {
          await taxConfigV2Api.deleteCategory(cat.categoryID);
        }
        const idx = this.workingCategories.findIndex((c) => c.categoryID === cat.categoryID);
        if (idx >= 0) this.workingCategories.splice(idx, 1);
        // Re-point its rules to uncategorized so a later Save all does not 400 on
        // an unknown categoryCode.
        this.workingRules.forEach((r) => {
          if (r.categoryCode === cat.categoryCode) r.categoryCode = null;
        });
        this.deleteDialog = false;
        this.deleteTarget = null;
        this.showSuccess(`ลบหมวด ${cat.categoryCode} แล้ว`);
      } catch (err) {
        this.showError(err?.message || 'ลบหมวดไม่สำเร็จ');
      } finally {
        this.deletingCategory = false;
      }
    },
    // ── Save all (bulk replace) ──
    buildRulePayload(rule) {
      // A rule row is a FIELD declaration: which field, in which category, holding what data type.
      return {
        categoryCode: rule.categoryCode ? String(rule.categoryCode).trim().toUpperCase() : null,
        fieldKey: String(rule.fieldKey || '').trim().toUpperCase(),
        label: rule.label || '',
        valueType: normalizeValueType(rule.valueType),
        sortOrder: Number(rule.sortOrder) || 0,
        isActive: rule.isActive !== false,
      };
    },
    async saveAll() {
      if (this.versionId == null) {
        this.showError('เวอร์ชันนี้ยังไม่ถูกบันทึก — บันทึกไม่ได้');
        return;
      }
      // Save is a full REPLACE of the version's rule set — an empty list deletes everything.
      // Only ever legitimate when the user really cleared the list, so make it deliberate.
      if (!this.workingRules.length && this.serverRuleCount > 0
        && !window.confirm(`บันทึกโดยไม่มีกติกาเลย จะลบกติกาทั้งหมด ${this.serverRuleCount} รายการของเวอร์ชันนี้ — ยืนยันหรือไม่?`)) {
        return;
      }
      this.saving = true;
      try {
        const payload = this.workingRules.map((r, idx) =>
          this.buildRulePayload({ ...r, sortOrder: r.sortOrder ?? idx }));
        await taxConfigV2Api.bulkSaveRules(this.versionId, payload);
        // Re-sync from server (fresh ids + canonical order).
        const full = await taxConfigV2Api.getFullConfig(this.versionId, true);
        this.buildWorkingSet(full);
        this.dirty = false;
        this.showSuccess('บันทึกกติกาทั้งเวอร์ชันแล้ว');
      } catch (err) {
        // Keep on-screen edits (server rolled back — nothing saved).
        this.showError(err?.message || 'บันทึกกติกาไม่สำเร็จ');
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.push('/TaxConfigurationV2');
    },
    showSuccess(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    showError(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.empty-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  text-align: center;
}

.empty-title {
  color: #6b7280;
  font-size: 0.95rem;
  margin: 0;
}

.save-bar {
  position: sticky;
  bottom: 0;
  z-index: 5;
  background: rgba(var(--v-theme-surface), 1);
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.06);
}

:deep(.rules-v2-table.my-data-table) {
  width: 100%;
}

:deep(.rules-v2-table.my-data-table thead th),
:deep(.rules-v2-table.my-data-table tbody td) {
  vertical-align: middle;
}
</style>
